import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Landmark, Plus, Pencil, X, Loader2, ChevronDown, XCircle } from 'lucide-react';

import CustomDatePicker from '../../components/CustomDatePicker';

import {
    fetchNatureOfGroups, fetchMasterGroups, fetchSubGroups, fetchApprovalStatusBank, fetchBankGrid,
    submitNewBank, submitUpdateBank,
    clearMasterGroups, clearSubGroups, clearSaveResult, clearUpdateResult, resetBankAccount,
    selectBankAccount,
} from '../../slices/accountsSlice/bankAccountSlice';

// ── Shared primitives (matches AddNewITCode / DCACreation conventions) ─────────
const inputCls = 'w-full px-3 py-2 border-2 rounded-xl text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:border-indigo-500 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 transition-all disabled:opacity-60';
const selectCls = inputCls + ' appearance-none pr-9 cursor-pointer';
const readonlyCls = 'w-full px-3 py-2 border-2 rounded-xl text-sm bg-gray-50 dark:bg-gray-900 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 cursor-not-allowed';

const Label = ({ children, required }) => (
    <label className="block text-xs font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-1">
        {children}{required && <span className="text-rose-500 ml-0.5">*</span>}
    </label>
);

const SelectWrap = ({ children, loading }) => (
    <div className="relative">
        {children}
        <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
            {loading ? <Loader2 className="h-4 w-4 text-indigo-500 animate-spin" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
        </div>
    </div>
);

const Btn = ({ onClick, disabled, loading, variant = 'primary', children }) => {
    const base = 'inline-flex items-center gap-2 font-semibold rounded-xl transition-all disabled:opacity-60 disabled:cursor-not-allowed px-4 py-2 text-sm';
    const variants = {
        primary: 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm',
        secondary: 'bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:border-indigo-400',
    };
    return (
        <button type="button" onClick={onClick} disabled={disabled || loading} className={`${base} ${variants[variant]}`}>
            {loading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {children}
        </button>
    );
};

const Badge = ({ children, color = 'gray' }) => {
    const colors = {
        green: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-700',
        red: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/20 dark:text-rose-400 dark:border-rose-700',
        amber: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-700',
        gray: 'bg-gray-50 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700',
    };
    return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${colors[color] || colors.gray}`}>{children}</span>;
};

const Card = ({ children, className = '' }) => (
    <div className={`bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm ${className}`}>{children}</div>
);

const EmptyState = ({ message }) => <div className="py-10 text-center text-gray-400 dark:text-gray-500 text-sm">{message}</div>;

const Modal = ({ title, onClose, children }) => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-indigo-600 to-violet-600 rounded-t-2xl sticky top-0 z-10">
                <h2 className="text-sm font-bold text-white">{title}</h2>
                <button onClick={onClose} className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"><X className="w-4 h-4 text-white" /></button>
            </div>
            <div className="p-5">{children}</div>
        </div>
    </div>
);

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const formatDateForAPI = (val) => {
    if (!val) return '';
    const d = val instanceof Date ? val : new Date(val);
    if (isNaN(d.getTime())) return '';
    return `${String(d.getDate()).padStart(2, '0')}-${MONTH_ABBR[d.getMonth()]}-${d.getFullYear()}`;
};
// Grid dates come back as "dd-Mon-yyyy" — parse explicitly rather than relying on
// native Date parsing or the picker's own numeric-month string parser.
const parseDDMonYYYY = (str) => {
    if (!str || typeof str !== 'string') return null;
    const parts = str.split('-');
    if (parts.length !== 3) return null;
    const day = parseInt(parts[0], 10);
    const monthIdx = MONTH_ABBR.findIndex((m) => m.toLowerCase() === parts[1].toLowerCase());
    const year = parseInt(parts[2], 10);
    if (isNaN(day) || monthIdx === -1 || isNaN(year)) return null;
    return new Date(year, monthIdx, day);
};

const ACCOUNT_TYPES = [
    { AcType: 'Over Draft', AcType_Desc: 'Over Draft' },
    { AcType: 'Savings', AcType_Desc: 'Savings' },
    { AcType: 'Current', AcType_Desc: 'Current' },
];
const BANK_TYPES = [
    { BankTypeId: 'Normal', BankTypeVal: 'Normal Bank A/C' },
    { BankTypeId: 'LCBG', BankTypeVal: 'LC and BG A/C' },
];

const statusBadge = (status) => {
    if (status === 'Approved') return <Badge color="green">Approved</Badge>;
    if (status === 'Rejected') return <Badge color="red">Rejected</Badge>;
    return <Badge color="amber">Pending</Badge>;
};

// ═════════════════════════════════════════════════════════════════════════════
// ADD BANK MODAL
// ═════════════════════════════════════════════════════════════════════════════

const AddBankModal = ({ onClose, currentUser, roleId, natureGroups, natureGroupsLoading }) => {
    const dispatch = useDispatch();
    const { masterGroups, masterGroupsLoading, subGroups, subGroupsLoading, saveLoading, saveResult, saveError } = useSelector(selectBankAccount);

    const [bankType, setBankType] = useState('');
    const [bankName, setBankName] = useState('');
    const [accountHolderName, setAccountHolderName] = useState('');
    const [accountNo, setAccountNo] = useState('');
    const [accountType, setAccountType] = useState('');
    const [accOpeningDate, setAccOpeningDate] = useState(new Date());
    const [balanceAsOn, setBalanceAsOn] = useState(new Date());
    const [location, setLocation] = useState('');
    const [openingBalance, setOpeningBalance] = useState('');
    const [minimumBalance, setMinimumBalance] = useState('');
    const [natureGroupId, setNatureGroupId] = useState('');
    const [groupId, setGroupId] = useState('');
    const [subGroupId, setSubGroupId] = useState('');
    const [ledgerValueType, setLedgerValueType] = useState('');

    useEffect(() => {
        if (saveResult) { toast.success('Bank account added successfully.'); dispatch(clearSaveResult()); onClose(); }
        if (saveError) { toast.error(saveError); dispatch(clearSaveResult()); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saveResult, saveError]);

    const handleNatureChange = (val) => {
        setNatureGroupId(val); setGroupId(''); setSubGroupId('');
        dispatch(clearMasterGroups());
        if (val) dispatch(fetchMasterGroups(val));
    };

    const handleGroupChange = (val) => {
        setGroupId(val); setSubGroupId('');
        dispatch(clearSubGroups());
        if (val) dispatch(fetchSubGroups(val));
    };

    const handleSubmit = () => {
        if (!bankType) { toast.warning('Select Bank Type.'); return; }
        if (!bankName.trim()) { toast.warning('Enter Bank Name.'); return; }
        if (!accountHolderName.trim()) { toast.warning('Enter Account Holder Name.'); return; }
        if (!accountNo.trim()) { toast.warning('Enter Account Number.'); return; }
        if (!accountType) { toast.warning('Select Account Type.'); return; }
        if (bankType === 'LCBG' && accountType !== 'Over Draft') { toast.warning('LCBG bank accounts must use Over Draft account type.'); return; }
        if (!(accOpeningDate instanceof Date)) { toast.warning('Select A/C Opening Date.'); return; }
        if (!(balanceAsOn instanceof Date)) { toast.warning('Select Balance As On date.'); return; }
        if (!location.trim()) { toast.warning('Enter Location.'); return; }
        if (!natureGroupId) { toast.warning('Select Nature of Group.'); return; }
        if (!groupId) { toast.warning('Select Group.'); return; }
        if (subGroups.length > 0 && !subGroupId) { toast.warning('Select Sub Group.'); return; }
        const balance = parseFloat(openingBalance) || 0;
        if (balance > 0) {
            const minBal = parseFloat(minimumBalance);
            if (!minimumBalance || isNaN(minBal) || minBal === 0) { toast.warning('Enter Minimum Balance.'); return; }
            if (balance < minBal) { toast.warning('Minimum Balance should not be greater than Opening Balance.'); return; }
        }
        if (!ledgerValueType) { toast.warning('Select Ledger Value Type.'); return; }

        dispatch(submitNewBank({
            BankName: bankName.trim(),
            AccountHolderName: accountHolderName.trim(),
            Accountno: accountNo.trim(),
            AccOpeningDate: accOpeningDate.toISOString(),
            AccountType: accountType,
            OpeningBalance: balance,
            BalanceAsOn: balanceAsOn.toISOString(),
            Banklocation: location.trim(),
            NatureGroupId: natureGroupId,
            Createdby: currentUser,
            Role: '',
            MinimumBalance: parseFloat(minimumBalance) || 0,
            GroupId: groupId,
            SubGroupId: subGroupId ? parseInt(subGroupId, 10) : 0,
            LedgerValueType: ledgerValueType,
            BankType: bankType,
            RoleId: parseInt(roleId, 10) || 0,
        }));
    };

    return (
        <Modal title="Create Bank" onClose={onClose}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <Label required>Bank Type</Label>
                    <SelectWrap>
                        <select className={selectCls} value={bankType} onChange={(e) => setBankType(e.target.value)} disabled={saveLoading}>
                            <option value="">-Please Select-</option>
                            {BANK_TYPES.map((t) => <option key={t.BankTypeId} value={t.BankTypeId}>{t.BankTypeVal}</option>)}
                        </select>
                    </SelectWrap>
                </div>
                <div>
                    <Label required>Bank Name</Label>
                    <input className={inputCls} value={bankName} onChange={(e) => setBankName(e.target.value)} disabled={saveLoading} />
                </div>
                <div>
                    <Label required>Account Holder Name</Label>
                    <input className={inputCls} value={accountHolderName} onChange={(e) => setAccountHolderName(e.target.value)} disabled={saveLoading} />
                </div>
                <div>
                    <Label required>Account No</Label>
                    <input className={inputCls} value={accountNo} onChange={(e) => setAccountNo(e.target.value.replace(/\D/g, ''))} disabled={saveLoading} inputMode="numeric" />
                </div>
                <div>
                    <Label required>Account Type</Label>
                    <SelectWrap>
                        <select className={selectCls} value={accountType} onChange={(e) => { setAccountType(e.target.value); setOpeningBalance(''); }} disabled={saveLoading}>
                            <option value="">-Please Select-</option>
                            {ACCOUNT_TYPES.map((t) => <option key={t.AcType} value={t.AcType}>{t.AcType_Desc}</option>)}
                        </select>
                    </SelectWrap>
                </div>
                <div>
                    <Label required>A/C Opening Date</Label>
                    <CustomDatePicker value={accOpeningDate} onChange={(d) => { setAccOpeningDate(d); }} format="DD-MMM-YYYY" placeholder="Select date" maxDate={new Date()} />
                </div>
                <div>
                    <Label required>Balance As On</Label>
                    <CustomDatePicker value={balanceAsOn} onChange={setBalanceAsOn} format="DD-MMM-YYYY" placeholder="Select date" maxDate={new Date()} minDate={accOpeningDate instanceof Date ? accOpeningDate : null} />
                </div>
                <div>
                    <Label required>Location</Label>
                    <input className={inputCls} value={location} onChange={(e) => setLocation(e.target.value)} disabled={saveLoading} />
                </div>
                <div>
                    <Label>Opening Balance</Label>
                    <input className={inputCls} value={openingBalance} onChange={(e) => setOpeningBalance(e.target.value)} disabled={saveLoading} inputMode="decimal" />
                </div>
                <div>
                    <Label required>Nature Of Group</Label>
                    <SelectWrap loading={natureGroupsLoading}>
                        <select className={selectCls} value={natureGroupId} onChange={(e) => handleNatureChange(e.target.value)} disabled={saveLoading}>
                            <option value="">-Please Select-</option>
                            {natureGroups.map((n) => <option key={n.NatureGroupId} value={n.NatureGroupId}>{n.NatureGroupName}</option>)}
                        </select>
                    </SelectWrap>
                </div>
                <div>
                    <Label required={parseFloat(openingBalance) > 0}>Minimum Balance</Label>
                    <input className={inputCls} value={minimumBalance} onChange={(e) => setMinimumBalance(e.target.value)} disabled={saveLoading} inputMode="decimal" />
                </div>
                <div>
                    <Label required>Master Group</Label>
                    <SelectWrap loading={masterGroupsLoading}>
                        <select className={selectCls} value={groupId} onChange={(e) => handleGroupChange(e.target.value)} disabled={saveLoading || !natureGroupId}>
                            <option value="">-Please Select-</option>
                            {masterGroups.map((g) => <option key={g.GroupId} value={g.GroupId}>{g.GroupName}</option>)}
                        </select>
                    </SelectWrap>
                </div>
                {subGroups.length > 0 && (
                    <div>
                        <Label required>Sub Group</Label>
                        <SelectWrap loading={subGroupsLoading}>
                            <select className={selectCls} value={subGroupId} onChange={(e) => setSubGroupId(e.target.value)} disabled={saveLoading}>
                                <option value="">-Please Select-</option>
                                {subGroups.map((s) => <option key={s.Id} value={s.Id}>{s.Name}</option>)}
                            </select>
                        </SelectWrap>
                    </div>
                )}
                <div className="md:col-span-2">
                    <Label required>Ledger Value Type</Label>
                    <div className="flex gap-4 mt-1">
                        {['Credit', 'Debit'].map((t) => (
                            <label key={t} className="flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-300">
                                <input type="radio" name="bankLedgerValueType" checked={ledgerValueType === t} onChange={() => setLedgerValueType(t)} disabled={saveLoading} />
                                {t}
                            </label>
                        ))}
                    </div>
                </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
                <Btn variant="secondary" onClick={onClose} disabled={saveLoading}>Cancel</Btn>
                <Btn onClick={handleSubmit} loading={saveLoading}>Submit</Btn>
            </div>
        </Modal>
    );
};

// ═════════════════════════════════════════════════════════════════════════════
// EDIT BANK MODAL
// ═════════════════════════════════════════════════════════════════════════════

const EditBankModal = ({ row, onClose, currentUser, roleId }) => {
    const dispatch = useDispatch();
    const { updateLoading, updateResult, updateError } = useSelector(selectBankAccount);

    const [location, setLocation] = useState(row.Banklocation || '');
    const [minimumBalance, setMinimumBalance] = useState(row.MinimumBalance ?? '');

    useEffect(() => {
        if (updateResult) { toast.success('Bank account updated successfully.'); dispatch(clearUpdateResult()); onClose(); }
        if (updateError) { toast.error(updateError); dispatch(clearUpdateResult()); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updateResult, updateError]);

    const handleSubmit = () => {
        if (!location.trim()) { toast.warning('Enter Location.'); return; }
        const minBal = parseFloat(minimumBalance);
        if (!minimumBalance || isNaN(minBal) || minBal === 0) { toast.warning('Enter Minimum Balance.'); return; }
        const openingBalance = parseFloat(row.OpeningBalance) || 0;
        if (openingBalance <= minBal) { toast.warning('Minimum Balance should not be greater than Opening Balance.'); return; }

        const openingDate = parseDDMonYYYY(row.ReturnAccOpeningDate || row.AccOpeningDate);
        const balAsOnDate = parseDDMonYYYY(row.ReturnBalanceAsOn || row.BalanceAsOn);

        dispatch(submitUpdateBank({
            Bankid: row.Bankid,
            Role: '',
            BankName: row.BankName,
            Banklocation: location.trim(),
            AccountType: row.AccountType,
            Accountno: row.Accountno,
            AccountHolderName: row.AccountHolderName,
            OpeningBalance: openingBalance,
            MinimumBalance: minBal,
            AccOpeningDate: openingDate ? openingDate.toISOString() : undefined,
            BalanceAsOn: balAsOnDate ? balAsOnDate.toISOString() : undefined,
            NatureGroupId: 0,
            Createdby: currentUser,
            RoleId: parseInt(roleId, 10) || 0,
        }));
    };

    return (
        <Modal title={`Edit Bank Details — ${row.BankName}`} onClose={onClose}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <Label>Bank Type</Label>
                    <input className={readonlyCls} value={row.BankType || ''} readOnly />
                </div>
                <div>
                    <Label>Bank Name</Label>
                    <input className={readonlyCls} value={row.BankName || ''} readOnly />
                </div>
                <div>
                    <Label>Account Holder Name</Label>
                    <input className={readonlyCls} value={row.AccountHolderName || ''} readOnly />
                </div>
                <div>
                    <Label>Account No</Label>
                    <input className={readonlyCls} value={row.Accountno || ''} readOnly />
                </div>
                <div>
                    <Label>Account Type</Label>
                    <input className={readonlyCls} value={row.AccountType || ''} readOnly />
                </div>
                <div>
                    <Label>A/C Opening Date</Label>
                    <input className={readonlyCls} value={row.AccOpeningDate || ''} readOnly />
                </div>
                <div>
                    <Label>Balance As On</Label>
                    <input className={readonlyCls} value={row.BalanceAsOn || ''} readOnly />
                </div>
                <div>
                    <Label>Opening Balance</Label>
                    <input className={readonlyCls} value={row.OpeningBalance ?? ''} readOnly />
                </div>
                <div>
                    <Label>Nature Of Group</Label>
                    <input className={readonlyCls} value={row.NatureGroupName || ''} readOnly />
                </div>
                <div>
                    <Label required>Location</Label>
                    <input className={inputCls} value={location} onChange={(e) => setLocation(e.target.value)} disabled={updateLoading} />
                </div>
                <div>
                    <Label required>Minimum Balance</Label>
                    <input className={inputCls} value={minimumBalance} onChange={(e) => setMinimumBalance(e.target.value)} disabled={updateLoading} inputMode="decimal" />
                </div>
                <div>
                    <Label>Group Name</Label>
                    <input className={readonlyCls} value={row.GroupName || ''} readOnly />
                </div>
                {row.SubGroupId ? (
                    <div>
                        <Label>Sub Group Name</Label>
                        <input className={readonlyCls} value={row.SubGroupName || ''} readOnly />
                    </div>
                ) : null}
                <div>
                    <Label>Ledger Value Type</Label>
                    <input className={readonlyCls} value={row.LedgerValueType || ''} readOnly />
                </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
                <Btn variant="secondary" onClick={onClose} disabled={updateLoading}>Cancel</Btn>
                <Btn onClick={handleSubmit} loading={updateLoading}>Submit</Btn>
            </div>
        </Modal>
    );
};

// ═════════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═════════════════════════════════════════════════════════════════════════════

const BankAccountCreation = ({ menuData }) => {
    const dispatch = useDispatch();
    const { userData } = useSelector((s) => s.auth);
    const roleId = userData?.roleId || userData?.RID || 0;
    const currentUser = userData?.userName || userData?.userId || userData?.UID || userData?.uid || '';

    const { natureGroups, natureGroupsLoading, grid, gridLoading, gridError, allowedActions } = useSelector(selectBankAccount);

    const [showAdd, setShowAdd] = useState(false);
    const [editRow, setEditRow] = useState(null);

    useEffect(() => {
        dispatch(fetchNatureOfGroups());
        dispatch(fetchBankGrid());
        dispatch(fetchApprovalStatusBank(roleId));
        return () => { dispatch(resetBankAccount()); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    const canAct = (row) => row.Status === 'Approved' && (!row.CloseStatus || row.CloseStatus === 'Rejected');

    const refreshGrid = () => dispatch(fetchBankGrid());

    const handleActionChange = (row, action) => {
        if (!action || action === 'Select' || action === '0') return;
        if (action === 'Edit') { setEditRow(row); return; }
        toast.info(`"${action}" isn't available on this page yet.`);
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
            <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-600 px-6 py-8 shadow-lg">
                <div className="max-w-7xl mx-auto flex items-center gap-3">
                    <div className="p-2 bg-white/20 rounded-xl"><Landmark className="h-5 w-5 text-white" /></div>
                    <div>
                        <h1 className="text-xl font-bold text-white">{menuData?.name || 'Bank Details'}</h1>
                        <p className="text-indigo-200 text-xs mt-0.5">Register and manage bank accounts</p>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6 py-6 space-y-4">
                <Card>
                    <div className="p-4 flex flex-wrap items-center justify-end gap-3">
                        <Btn onClick={() => setShowAdd(true)}><Plus className="h-3.5 w-3.5" />Add Bank</Btn>
                    </div>
                </Card>

                <Card>
                    {gridLoading ? (
                        <div className="py-14 flex justify-center"><Loader2 className="h-6 w-6 text-indigo-500 animate-spin" /></div>
                    ) : gridError ? (
                        <div className="py-10 text-center text-sm text-rose-500">{gridError}</div>
                    ) : grid.length === 0 ? (
                        <EmptyState message="No Bank Accounts found." />
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="bg-gray-50 dark:bg-gray-900/40 text-left">
                                        {['Bank Name', 'Type', 'Account No', 'A/C Holder', 'Location', 'Opening Balance', 'Min Balance', 'Balance As On', 'Status', ''].map((h) => (
                                            <th key={h} className="px-3 py-2.5 text-xs font-bold text-gray-500 uppercase tracking-wider">{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50 dark:divide-gray-700">
                                    {grid.map((row) => (
                                        <tr key={row.Bankid} className="hover:bg-indigo-50/40 dark:hover:bg-indigo-900/10 transition-colors">
                                            <td className="px-3 py-2.5 text-gray-800 dark:text-gray-100">{row.BankName}</td>
                                            <td className="px-3 py-2.5 text-gray-600 dark:text-gray-300">{row.AccountType}</td>
                                            <td className="px-3 py-2.5 font-mono text-gray-700 dark:text-gray-300">{row.Accountno}</td>
                                            <td className="px-3 py-2.5 text-gray-600 dark:text-gray-300">{row.AccountHolderName}</td>
                                            <td className="px-3 py-2.5 text-gray-500 text-xs">{row.Banklocation}</td>
                                            <td className="px-3 py-2.5 text-right font-semibold">{row.OpeningBalance}</td>
                                            <td className="px-3 py-2.5 text-right">{row.MinimumBalance}</td>
                                            <td className="px-3 py-2.5 text-gray-500 text-xs">{row.BalanceAsOn}</td>
                                            <td className="px-3 py-2.5">{statusBadge(row.Status)}</td>
                                            <td className="px-3 py-2.5">
                                                {canAct(row) ? (
                                                    allowedActions.length > 0 ? (
                                                        <SelectWrap>
                                                            <select
                                                                className={selectCls + ' py-1 text-xs'}
                                                                defaultValue=""
                                                                onChange={(e) => { handleActionChange(row, e.target.value); e.target.value = ''; }}
                                                            >
                                                                <option value="">-Select-</option>
                                                                {allowedActions.map((a, i) => <option key={i} value={a.Value}>{a.Text}</option>)}
                                                            </select>
                                                        </SelectWrap>
                                                    ) : (
                                                        <button onClick={() => setEditRow(row)} className="p-1.5 rounded-lg text-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-900/30" title="Edit">
                                                            <Pencil className="h-3.5 w-3.5" />
                                                        </button>
                                                    )
                                                ) : (
                                                    <span className="text-xs text-gray-300 dark:text-gray-600 inline-flex items-center gap-1">
                                                        <XCircle className="h-3 w-3" /> —
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Card>
            </div>

            {showAdd && (
                <AddBankModal
                    onClose={() => { setShowAdd(false); refreshGrid(); }}
                    currentUser={currentUser} roleId={roleId}
                    natureGroups={natureGroups} natureGroupsLoading={natureGroupsLoading}
                />
            )}
            {editRow && (
                <EditBankModal
                    row={editRow} onClose={() => { setEditRow(null); refreshGrid(); }}
                    currentUser={currentUser} roleId={roleId}
                />
            )}
        </div>
    );
};

export default BankAccountCreation;
